import { motion } from "framer-motion";
import { Award, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { FiLoader } from "react-icons/fi";
import { IoIosCloseCircle } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../contextApi/NotificationProvider";
import {
  getDashboradData,
  getUserDetails,
} from "../../redux/ReduxSlices/authSlice";
import { getSubscriptionPlans } from "../../redux/ReduxSlices/subscriptionPlanSlice";
import PaymentPopup from "./PaymentPopup";

export default function PricingPlans() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.auth);
  const creditPoint = userData?.company_info?.credit_point;
  const { dashboardData } = useSelector((state) => state.auth);
  const dashboardCount = dashboardData?.recent_transactions;
  const token = localStorage.getItem("token");
  const [currency, setCurrency] = useState("INR");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { showNotification } = useNotification();
  const [currentPlans, setCurrentPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const userType = localStorage.getItem("userType");
  const [latestPlan, setLatestPlan] = useState("");

  useEffect(() => {
    dispatch(getDashboradData());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  const freecodedPlan = {
    title: "Starter",
    price: "Free",
    sub_text: "Try before you buy",
    description: [
      "2 Free Assessment Credits",
      "Complete Personality Reports",
      "Email Support",
      "No Credit Card Required"
    ],
    highlighted: false,
    button_txt:
      creditPoint < 2 || dashboardCount?.length !== 0
        ? "Free plan Expired"
        : "Currently Used",
  };

  const handleClick = (plan) => {
    if (plan.price === "Free") {
      return;
    }

    if (plan.button_txt === "Contact Sales") {
      window.open("https://selfsync.ai/contact-us", "_blank");
    } else if (plan.button_txt === "currently used") {
      // show
      showNotification("You have 2 Credits in your account", "warning", 8000);
    } else if (plan.button_txt === "Free plan Expired") {
      // show
      showNotification(
        "Your free trial expired, please purchase new plan",
        "error",
        8000
      );
    } else {
      setSelectedPlan(plan);
      setShowModal(true);
    }
  };
  const closeModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
  };

  const getSubscriptions = async () => {
    setLoading(true);
    try {
      let response = await dispatch(getSubscriptionPlans()).unwrap();
      setSubscriptionPlans(response.data.packages);
      let current = response.data?.latest_payment?.subscription_pk
        ? response.data.latest_payment.subscription_pk
        : "";
      setLatestPlan(current);
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubscriptions();
  }, []);

  useEffect(() => {
    if (subscriptionPlans.length > 0) {
      let plans = subscriptionPlans.filter((plan) => {
        return plan.currency == currency && plan.is_active == true;
      });
      const sortedSubscriptions = plans.sort(
        (a, b) => a.num_of_assessments - b.num_of_assessments
      );
      sortedSubscriptions.push({
        title: "Enterprise",
        price: "Custom",
        sub_text: "Tailored for your needs",
        description: [
          "Unlimited Assessments",
          "White-Label Solution",
          "Custom AI Models",
          "24/7 Premium Support",
          "On-Premise Deployment"
        ],
        button_txt: "Contact Sales",
        highlighted: false,
        yearly_only: false,
        plan_type: "company",
      });
      setCurrentPlans(sortedSubscriptions);
    }
  }, [currency, subscriptionPlans]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // const allPlans = [freecodedPlan, ...currentPlans];
  let allPlans = [];
  if (userType == "individual") {
    let plans = currentPlans.filter((p, i) => p.plan_type == "individual");
    allPlans = [...plans];
  } else {
    let plans = currentPlans.filter((p, i) => p.plan_type == "company");
    allPlans = [freecodedPlan, ...plans];
  }
  return (
    <div className="flex flex-col items-center px-4 py-12 bg-gradient-to-br from-white to-gray-50">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <FiLoader className="animate-spin text-[#822bda] text-4xl" />
        </div>
      )}
      {!loading && (
        <>
          <motion.h2
            className="text-3xl md:text-4xl font-semibold mb-2 max-md:mt-3 text-center"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {userType == "individual"
              ? "Your Growth, One Simple Plan Away"
              : "Transparent Pricing for Every Team"}
          </motion.h2>
          <motion.p
            className="text-md md:text-lg text-gray-600 mb-8 text-center max-w-3xl mt-1"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {userType == "individual"
              ? "Understand yourself, unlock your potential, and take control of your future—with just one powerful plan."
              : "Get access to our full suite of assessments, feedback tools, and analytics. Whether you're a startup or an enterprise, we have the right solution for you."}
          </motion.p>

          <motion.div
            className="relative inline-flex p-1 bg-gray-100 rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            {/* Sliding background */}
            <div
              className={`absolute inset-y-0 my-1 btn-gradient-help  transition-all duration-300 ease-in-out ${currency === "INR" ? "left-1 w-1/2" : "left-1/2 w-1/2"
                }`}
              style={{ height: "calc(100% - 0.5rem)" }}
            ></div>

            {/* Buttons */}
            <button
              className={`relative px-6 py-1.5 text-sm font-medium rounded-full z-10 transition-colors duration-200 inrPlans ${currency === "INR"
                ? "text-white"
                : "text-gray-600 hover:text-purple-600"
                }`}
              onClick={() => setCurrency("INR")}
            >
              ₹ INR
            </button>
            <button
              className={`relative px-6 py-1.5 text-sm font-medium rounded-full z-10 transition-colors duration-200 usdPlans ${currency === "USD"
                ? "text-white"
                : "text-gray-600 hover:text-purple-600"
                }`}
              onClick={() => setCurrency("USD")}
            >
              $ USD
            </button>
          </motion.div>

          <motion.div
            className="w-full flex justify-center"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "backOut" }}
          >
            {/* <div className={userType == "individual" ? "flex w-[22%] max-md:w-full" : "grid grid-cols-1 md:grid-cols-4 gap-6"}>
              {allPlans.length > 0 &&
                allPlans.map((plan, idx) => (
                  <div
                    key={idx}
                    className={`${userType == "individual" && "flex items-start w-full"} ${plan.subscription_pk === latestPlan && creditPoint > 0
                        ? "border-green-800 ring-2 ring-green-500"
                        : "border"
                      } relative border rounded-2xl p-6 flex flex-col justify-between shadow-sm transition-transform transform hover:scale-105 hover:shadow-lg ${plan.highlighted
                        ? "bg-indigo-50 border-indigo-300"
                        : "bg-white"
                      }`}
                  >
                    {plan.subscription_pk === latestPlan && creditPoint > 0 && (
                      <span className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md whitespace-nowrap">
                        Your Current Plan
                      </span>
                    )}
                    {plan.title === "200 Assessments" && (
                      <span className="absolute top-2 right-2 bg-yellow-400 text-xs font-semibold px-2 py-1 rounded-md shadow-md">
                        Popular
                      </span>
                    )}

                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        {plan.title}
                      </h3>

                      <p className="text-3xl font-bold mb-1">
                        {typeof plan.price === "number"
                          ? formatCurrency(plan.price)
                          : plan.price}
                        <span className="text-base font-normal">
                          {typeof plan.price === "number" && (
                            <span className="text-base font-normal">
                              {currency}
                            </span>
                          )}
                        </span>
                      </p>

                      {plan.sub_text && (
                        <p className="text-sm text-gray-500 mb-4 whitespace-pre-line">
                          {plan.sub_text}
                        </p>
                      )}

                      <ul className="mb-6 space-y-2 text-sm">
                        {plan.description.map((item, index) => (
                          <li
                            key={index}
                            className="flex items-start space-x-2"
                          >
                            <span className="text-green-600 font-bold">✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button
                      className={
                        plan.button_txt === "Free plan Expired"
                          ? "border px-4 py-2 rounded-full font-medium text-sm w-full mt-auto bg-gray-400 text-white"
                          : "border px-4 py-2 rounded-full font-medium text-sm w-full mt-auto bg-purple-600 text-white"
                      }
                      onClick={() => handleClick(plan)}
                    >
                      {plan.button_txt}
                    </button>
                  </div>
                ))}
            </div> */}

            <section className="py-20 max-md:py-16 -mt-10">
              <div className="max-w-7xl mx-auto px-6">
                <div
                  className={`grid ${userType === "individual"
                    ? "flex justify-center items-center"
                    : "grid-cols-1 md:grid-cols-5"
                    } gap-6`}
                >
                  {allPlans.map((plan, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: idx * 0.1 }}
                      viewport={{ once: true }}
                      className={`relative flex flex-col rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${plan.highlighted
                        ? "bg-gradient-to-br from-purple-600 to-cyan-600 text-white shadow-2xl ring-4 ring-purple-200"
                        : "bg-white shadow-lg border border-gray-200 hover:shadow-xl"
                        }`}
                    >
                      {/* Banners */}
                      {plan.subscription_pk === latestPlan &&
                        creditPoint > 0 && (
                          <span className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-yellow-400 text-purple-900 text-xxs font-bold px-2 py-1 rounded-full shadow-md">
                            Your Current Plan
                          </span>
                        )}
                      {plan.title === "Scale Business" && (
                        <span className="absolute -top-3 right-14 bg-yellow-400 text-purple-900 text-xs font-semibold px-3 py-1 rounded-full shadow-md flex items-center">
                          <Award className="w-3 h-3" />  Best Value
                        </span>
                      )}
                      {plan.title === "Growing Team" && (
                        <span className="absolute -top-3 right-14 bg-yellow-400 text-purple-900 text-xs font-semibold px-3 py-1 rounded-full shadow-md flex items-center">
                          <Award className="w-3 h-3" />  Most Popular
                        </span>
                      )}

                      {/* Plan Content */}
                      <h3 className="text-lg font-bold mb-2">{plan.title}</h3>
                      <div className="text-2xl font-bold mb-2">
                        {typeof plan.price === "number"
                          ? formatCurrency(plan.price)
                          : plan.price}
                        <span className="text-sm font-normal ml-1">
                          {plan.price > 1200 ? currency : ""}
                        </span>
                      </div>
                      {plan.sub_text && (
                        <p className="text-sm mb-4">{plan.sub_text}</p>
                      )}
                      <ul className="mb-6 space-y-2 text-sm">
                        {plan.description.map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check
                              className={`w-4 h-4 mt-0.5 ${plan.highlighted
                                ? "text-yellow-400"
                                : "text-green-600"
                                }`}
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        className={`w-full py-2 px-2 rounded-full text-sm font-semibold mt-auto  ${plan.button_txt === "Free plan Expired"
                          ? "bg-gray-400 text-white"
                          : plan.highlighted
                            ? "btn-gradient transition-all border"
                            : "btn-gradient text-white"
                          }`}
                        onClick={() => handleClick(plan)}
                      >
                        {plan.button_txt}
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          </motion.div>

          <motion.div
            className="mt-20 text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h3 className="text-3xl font-semibold mb-6">
              {userType == "individual"
                ? "Still have questions about the plan?"
                : "Still not sure which plan is right for you?"}
            </h3>
            <p className="text-gray-600 text-base mb-4">
              {userType == "individual"
                ? "Your growth starts here. We’ll help you choose the right plan to unlock your full potential—just get in touch!"
                : "We're here to help! Our team can guide you in selecting a plan tailored to your organization's goals. Don't hesitate to get in touch."}
            </p>
            <button
              onClick={() =>
                window.open("https://selfsync.ai/contact-us", "_blank")
              }
              className="btn-gradient text-white px-6 py-3 rounded-full font-medium shadow-md"
            >
              Talk to Our Team
            </button>
          </motion.div>
          {/* Features Overview */}

          <motion.div
            className="mt-16 max-w-6xl text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h3 className="text-3xl font-semibold mb-6">
              Why Companies Choose Us
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left text-gray-700">
              <div>
                <h4 className="font-bold text-lg mb-2">
                  Comprehensive Assessments
                </h4>
                <p>
                  Measure knowledge, skills, and behavioral traits with
                  scientifically designed tools.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Real-time Reporting</h4>
                <p>
                  Get instant insights into team performance and development
                  areas.
                </p>
              </div>
              {userType != "individual" && (
                <div>
                  <h4 className="font-bold text-lg mb-2">Custom Branding</h4>
                  <p>
                    Keep your identity intact with logo placement and company
                    colors on all assessments.
                  </p>
                </div>
              )}
              <div>
                <h4 className="font-bold text-lg mb-2">Secure & Compliant</h4>
                <p>
                  We follow industry best practices to ensure your data is safe
                  and confidential.
                </p>
              </div>
              {userType != "individual" && (
                <div>
                  <h4 className="font-bold text-lg mb-2">Dedicated Support</h4>
                  <p>
                    From setup to scaling, our customer success team is always
                    by your side.
                  </p>
                </div>
              )}
              {userType != "individual" && (
                <div>
                  <h4 className="font-bold text-lg mb-2">
                    HR & LMS Integrations
                  </h4>
                  <p>
                    Plug us into your existing systems for a seamless employee
                    assessment journey.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={closeModal}
              className="absolute -top-64 right-1 p-1 text-black hover:text-gray-300 z-[60] max-md:-top-[26rem] max-md:right-3"
              aria-label="Close modal"
            >
              <IoIosCloseCircle className="text-3xl" />
            </button>
            <PaymentPopup
              selectedPlan={selectedPlan}
              closeModal={closeModal}
              setShowModal={setShowModal}
              currency={currency}
            />
          </div>
        </div>
      )}
    </div>
  );
}
